import { fillDriveFolderTokens, type DriveFolderTokens } from "./driveFolderTemplate.js";
import { sanitizeDriveName } from "./driveDealFolder.js";
import { parseDriveFolderRecipeCompact, printDriveFolderRecipeCompact } from "./driveFolderRecipeCompact.js";
import {
  DRIVE_MATCH_KEYS,
  makeDriveFolderNode,
  type DriveDealFacts,
  type DriveFolderNode,
  type DriveFolderRecipe,
  type DriveMatchKey,
} from "./driveFolderRecipeModel.js";

export {
  DRIVE_MATCH_KEYS,
  DRIVE_MATCH_OPTIONS,
  inferDriveFolderMatch,
  makeDriveFolderNode,
  newDriveFolderNodeId,
  type DriveDealFacts,
  type DriveFolderNode,
  type DriveFolderRecipe,
  type DriveMatchKey,
} from "./driveFolderRecipeModel.js";

export { parseDriveFolderRecipeCompact, printDriveFolderRecipeCompact } from "./driveFolderRecipeCompact.js";

const DEFAULT_COMPACT =
  "{Pending,Cancelled/{RTO Rejected,Customer Cancelled}/{year},Delivered/{RTO,Finance,Cash}/{year}/{month}}/{display_name} · {deal_id_short}";

export function defaultDriveFolderRecipe(): DriveFolderRecipe {
  return parseDriveFolderRecipeCompact(DEFAULT_COMPACT) ?? { version: 1, nodes: [] };
}

export function parseDriveFolderRecipe(raw: string): DriveFolderRecipe {
  const trimmed = raw.trim();
  if (!trimmed) return defaultDriveFolderRecipe();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const recipe = recipeFromUnknown(JSON.parse(trimmed) as unknown);
      if (recipe) return recipe;
    } catch {
      /* compact or linear */
    }
  }
  if (/\{[^{}\n]+,[^{}\n]+\}/.test(trimmed) || /\{[^{}]*\//.test(trimmed)) {
    const compact = parseDriveFolderRecipeCompact(trimmed);
    if (compact) return compact;
  }
  return linearDriveFolderRecipe(trimmed);
}

export function recipeFromUnknown(raw: unknown): DriveFolderRecipe | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (row.version !== 1 || !Array.isArray(row.nodes)) return null;
  return { version: 1, nodes: row.nodes.map((n) => nodeFromUnknown(n)).filter((n): n is DriveFolderNode => Boolean(n)) };
}

function nodeFromUnknown(raw: unknown): DriveFolderNode | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const name = typeof row.name === "string" ? row.name : "";
  if (!name.trim()) return null;
  const matchKey = DRIVE_MATCH_KEYS.includes(row.matchKey as DriveMatchKey) ? (row.matchKey as DriveMatchKey) : null;
  const matchValue = typeof row.matchValue === "string" && row.matchValue.trim() ? row.matchValue.trim() : null;
  const children = Array.isArray(row.children)
    ? row.children.map((c) => nodeFromUnknown(c)).filter((n): n is DriveFolderNode => Boolean(n))
    : [];
  return {
    id: typeof row.id === "string" && row.id.trim() ? row.id : makeDriveFolderNode(name, null).id,
    name,
    matchKey,
    matchValue: matchKey ? matchValue : null,
    children,
  };
}

export function linearDriveFolderRecipe(template: string): DriveFolderRecipe {
  const segs = template.split("/").map((s) => s.trim()).filter(Boolean);
  let children: DriveFolderNode[] = [];
  for (let i = segs.length - 1; i >= 0; i--) {
    children = [makeDriveFolderNode(segs[i]!, null, children)];
  }
  return { version: 1, nodes: children };
}

export function serializeDriveFolderRecipe(recipe: DriveFolderRecipe, format: "json" | "compact"): string {
  if (format === "compact") return printDriveFolderRecipeCompact(recipe);
  return JSON.stringify(recipe, null, 2);
}

export function expandDriveFolderRecipe(
  recipe: DriveFolderRecipe,
  tokens: DriveFolderTokens,
  facts: DriveDealFacts
): string[] {
  return expandRecipeNodes(recipe.nodes, tokens, facts);
}

function expandRecipeNodes(nodes: DriveFolderNode[], tokens: DriveFolderTokens, facts: DriveDealFacts): string[] {
  const cases = nodes.filter((n) => n.matchKey);
  const always = nodes.filter((n) => !n.matchKey);
  const segs: string[] = [];
  if (cases.length) {
    const factOf = (n: DriveFolderNode) => (n.matchKey ? facts[n.matchKey] : "");
    const pick =
      cases.find((n) => n.matchValue && n.matchValue !== "*" && factOf(n) === n.matchValue) ??
      cases.find((n) => n.matchValue === "*");
    if (pick) segs.push(...expandOne(pick, tokens, facts));
  }
  for (const n of always) segs.push(...expandOne(n, tokens, facts));
  return segs;
}

function expandOne(node: DriveFolderNode, tokens: DriveFolderTokens, facts: DriveDealFacts): string[] {
  const name = sanitizeDriveName(fillDriveFolderTokens(node.name, tokens), "", 120);
  const rest = expandRecipeNodes(node.children, tokens, facts);
  return name ? [name, ...rest] : rest;
}

export function expandDriveFolderPath(raw: string, tokens: DriveFolderTokens, facts: DriveDealFacts): string[] {
  return expandDriveFolderRecipe(parseDriveFolderRecipe(raw), tokens, facts);
}
